import React from 'react';

// Import JS Modules
import PropTypes from 'prop-types';
import parseQueryString from '../../_utils/parse-query-string';
import UrlBuilder from '../../_utils/url-building';
import setCanonicalUrl from '../../_utils/tools/canonical-url';
import merge from 'lodash/merge';
import QuantityPricingTools from '../quantity-pricing-tools';
import PriceAndProductCreate from '../price-and-product-create';

class GroupedCategoryBase extends React.PureComponent {
  constructor(props) {
    super(props);

    this.productAndFilters = React.createRef();

    this.state = {
      filters: {},
      options: {},
      copies: this.getInitialCopies(this.props.data.products),
    };
  }

  componentDidMount() {
    this.setDefaultFilters();
  }

  getCurrentProductIdAccordingToUrl = () => {
    return parseInt(parseQueryString().product, 10);
  };

  getCurrentShelfFiltersFromUrl = () => {
    // Try to map Shelf Filters to Item's
    // If a map fails, the corresponding filter will be unselected
    return Object.entries(parseQueryString()).reduce(
      (filters, [key, value]) => {
        value = value.replace(/\"/g, '');

        const product = this.props.data.products.find(
          p => (p.filters[key] || '').replace(/\"/g, '') === value
        );

        if (product) {
          filters[key] = product.filters[key];
        }

        return filters;
      },
      {}
    );
  };

  getDefaultFilters = () => {
    const defaultFilters = {};
    this.props.data.filters
      .filter(function(filter) {
        return filter.options.some(option => option.default === true);
      })
      .forEach(function(filter) {
        const defaultOptionTitle =
          (filter.options || {}).find(option => option.default === true) || {};
        if (Object.keys(defaultOptionTitle).length > 0) {
          defaultFilters[filter.title] = defaultOptionTitle.title;
        }
      });
    return defaultFilters;
  };

  setDefaultFilters = (
    productId = this.getCurrentProductIdAccordingToUrl()
  ) => {
    const productFilters = (() => {
      const product = this.props.data.products.find(p => p.id == productId);

      if (product) {
        return product.filters;
      }
    })();

    const mergedFilters = merge(
      this.getDefaultFilters(),
      this.getCurrentShelfFiltersFromUrl()
    );

    this.setState({
      filters: productFilters || mergedFilters,
    });

    const { canonical_url_parameters: canonicalUrlParameters } = this.props;

    setCanonicalUrl(window, canonicalUrlParameters);
  };

  getInitialCopies = products => {
    const currentProductId = this.getCurrentProductIdAccordingToUrl();
    const product =
      products.find(prod => prod.id === currentProductId) || products[0];

    if (!product) return;

    const [theme] = product.themes,
      defaultCopies = product.set_copies && theme && theme.default_copies;

    return (
      defaultCopies ||
      new QuantityPricingTools(product).currentOrFirstAvailableCopies(1)
    );
  };

  handleCreateNowClick = (e, button) => {
    const currentProducts = this.getCurrentProducts();

    if (currentProducts.length > 1) {
      e.preventDefault();

      this.productAndFilters.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

      this.setState(
        {
          validateOptionsChosen: true,
        },
        () => {
          button.reset();
        }
      );
    }
  };

  getCurrentProducts = () => {
    const { filters, selectedDeliveryOptionsState } = this.state;
    const { products } = this.props.data;
    return products.filter((product) => {
      let hasSelectedDelivery;
      if (selectedDeliveryOptionsState === null) {
        hasSelectedDelivery = product;
      } else {
        hasSelectedDelivery = product.delivery_options.includes(
          parseInt(selectedDeliveryOptionsState),
        );
      }

      if (!filters) {
        return hasSelectedDelivery;
      }

      return (
        hasSelectedDelivery &&
        Object.entries(filters).every(
          ([key, value]) => product.filters[key] === value,
        )
      );
    });
  };

  handleOptionChange = (name, value, asset) => {
    this.setState({
      options: {
        ...this.state.options,
        [name]: {
          value: value,
          asset: asset
        }
      },
    });
  };

  handleFilterCriteronSelected = (title, criteron) => {
    this.setState({
      filters: {
        ...this.state.filters,
        [title]: criteron,
      },
      options: {}
    });
  };

  setInnerHtmlDescription(description) {
    return (
      <div
        className="product-description-text"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  }

  renderPriceAndProductCreate = (
    currentProducts,
    selectedTheme,
    calendarStartDate = null,
    copies,
    options,
    variantName,
    selectedDeliveryOptions,
    groupedCategoryNewUi,
  ) => {
    return (
      <PriceAndProductCreate
        products={currentProducts}
        theme={selectedTheme && selectedTheme.name}
        calendar_start_date={calendarStartDate}
        options={options}
        copies={copies}
        onClick={this.handleCreateNowClick}
        variantName={variantName}
        selectedDeliveryOptions={selectedDeliveryOptions}
        groupedCategoryNewUi={groupedCategoryNewUi}
      />
    );
  };

  setPageAttributesForProduct = (...currentProductParams) => {

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // dont' start manipulating the push state until we've seen the default product
    this.pushUrlState.call(
      this,
      !this.defaultProductHasBeenSeen,
      ...currentProductParams
    );
    this.defaultProductHasBeenSeen = true;
  };

  pushUrlState = (
    firstTime,
    currentProduct,
    selectedTheme,
    defaultDesignCode,
    variantName,
    selectedDeliveryOptions
  ) => {
    if (
      window.history &&
      (this.getCurrentProductIdAccordingToUrl() != currentProduct.id ||
        firstTime)
    ) {
      // url doesn't match the current product
      const params = {
        product: currentProduct.slug || currentProduct.id, // || currentProduct.id for zero-downtime only
        theme: selectedTheme && selectedTheme.name,
        ...(defaultDesignCode && { design_code: defaultDesignCode }),
      };

      if (variantName) {
        params.variant_name = variantName
      }

      if (selectedDeliveryOptions) {
        params.selected_delivery_options = this.state.selectedDeliveryOptionsState
      }

      // We want to persist certain params for tracking / analytics purposes
      const queryParams = parseQueryString();
      const whitelistedParamsRegex = /^utm_.*$|^veh$|^adid$/;

      for (const key in queryParams) {
        if (key.match(whitelistedParamsRegex)) {
          params[key] = queryParams[key];
        }
      }

      const path = `${window.location.origin +
        window.location.pathname}?${UrlBuilder.buildURLQuery(params)}`;
      if (firstTime) {
        window.history.replaceState(
          {
            product_id: currentProduct.id,
          },
          '',
          path
        );
      } else {
        window.history.pushState(
          {
            product_id: currentProduct.id,
          },
          '',
          path
        );
      }
    }
    const { canonical_url_parameters: canonicalUrlParameters } = this.props;

    setCanonicalUrl(window, canonicalUrlParameters);
  };

  handlePopState = (e) => {
    const { state } = e;
    if (state) {
      this.setDefaultFilters(state.product_id);
    }
  };

  handlePageShow(e) {
    let historyTraversal =
      e.persisted ||
      (typeof window.performance != 'undefined' &&
        window.performance.getEntriesByType('navigation')[0].type ===
          'back_forward');
    if (historyTraversal) {
      window.location.reload(true);
    }
  }
}

GroupedCategoryBase.propTypes = {
  canonical_url_parameters: PropTypes.arrayOf(PropTypes.string),
};

GroupedCategoryBase.defaultProps = {
  canonical_url_parameters: [],
};

export default GroupedCategoryBase;

